import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'

import { SELECT_FILTER_OPTION_VALUE_MAP, type SelectFilterOptionValue } from '@/components/select-filter/constants'

import { CASE_ARCHIVE_STATUS_MAP } from '@/lib/queries/business/constants'
import { getCaseFiltersQuery } from '@/lib/queries/business/v2/case/filters/queries'

import { useCasesTableContext } from '../../contexts'
import { type CasesFilterChangeHandler, type CasesFilterProps } from './cases-filter'

export type UseCasesFilterParams = CasesFilterProps

export const useCasesFilter = (params: UseCasesFilterParams) => {
  const {
    keyword = '',
    tags = [],
    tagFilterOption = SELECT_FILTER_OPTION_VALUE_MAP.or,
    processingTimes = [],
    processingTimeFilterOption = SELECT_FILTER_OPTION_VALUE_MAP.or,
    caseTypes = [],
    statuses = [],
    assignees = [],
    assigneeFilterOption = SELECT_FILTER_OPTION_VALUE_MAP.or,
    disabled = false,
    onChange,
  } = params
  const currentFilterParams = useMemo(
    () => ({
      keyword,
      tagFilterOption,
      tags,
      processingTimes,
      processingTimeFilterOption,
      caseTypes,
      statuses,
      assignees,
      assigneeFilterOption,
    }),
    [
      keyword,
      tags,
      processingTimes,
      caseTypes,
      statuses,
      tagFilterOption,
      processingTimeFilterOption,
      assignees,
      assigneeFilterOption,
    ]
  )

  const { tableType } = useCasesTableContext()

  const { data: dataFilters } = useQuery(
    getCaseFiltersQuery({
      archiveStatus: tableType === 'active' ? CASE_ARCHIVE_STATUS_MAP.active : CASE_ARCHIVE_STATUS_MAP.archived,
      isEoir: params.isEoir,
    })
  )

  const caseTypeFilterItems = dataFilters?.caseTypes ?? []
  const statusFilterItems = dataFilters?.status ?? []
  const tagFilterItems = dataFilters?.tags ?? []
  const processingTimeFilterItems = dataFilters?.processingTime ?? []
  const assigneeFilterItems =
    dataFilters?.assignees.map((filterItem) => ({
      ...filterItem,
      filterValue: String(filterItem.filterValue),
    })) ?? []

  const isFilterApplied = useMemo(
    () =>
      !!keyword ||
      tags.length > 0 ||
      processingTimes.length > 0 ||
      statuses.length > 0 ||
      assignees.length > 0 ||
      caseTypes.length > 0,
    [keyword, tags, processingTimes, statuses, assignees, caseTypes]
  )

  const handleChange: CasesFilterChangeHandler = useCallback(
    (params) => {
      onChange?.(params)
    },
    [onChange]
  )

  const handleChangeKeyword = useCallback(
    (keyword: string) => {
      handleChange?.({ ...currentFilterParams, keyword })
    },
    [handleChange, currentFilterParams]
  )

  const handleChangeCaseTypes = useCallback(
    (caseTypes: string[]) => {
      handleChange?.({ ...currentFilterParams, caseTypes })
    },
    [handleChange, currentFilterParams]
  )

  const handleChangeStatuses = useCallback(
    (statuses: string[]) => {
      onChange?.({ ...currentFilterParams, statuses })
    },
    [onChange, currentFilterParams]
  )

  const handleChangeTags = useCallback(
    (tags: string[]) => {
      handleChange?.({
        ...currentFilterParams,
        tags,
      })
    },
    [handleChange, currentFilterParams]
  )

  const handleChangeProcessingTimes = useCallback(
    (processingTimes: string[]) => {
      handleChange?.({ ...currentFilterParams, processingTimes })
    },
    [handleChange, currentFilterParams]
  )

  const handleChangeAssignees = useCallback(
    (assignees: string[]) => {
      handleChange?.({ ...currentFilterParams, assignees })
    },
    [handleChange, currentFilterParams]
  )

  const handleChangeTagFilterOption = useCallback(
    (option: SelectFilterOptionValue) => {
      handleChange?.({ ...currentFilterParams, tagFilterOption: option })
    },
    [handleChange, currentFilterParams]
  )

  const handleChangeProcessingTimesFilterOption = useCallback(
    (option: SelectFilterOptionValue) => {
      handleChange?.({
        ...currentFilterParams,
        processingTimeFilterOption: option,
      })
    },
    [handleChange, currentFilterParams]
  )

  const handleChangeAssigneesFilterOption = useCallback(
    (option: SelectFilterOptionValue) => {
      handleChange?.({
        ...currentFilterParams,
        assigneeFilterOption: option,
      })
    },
    [handleChange, currentFilterParams]
  )

  const handleReset = useCallback(() => {
    handleChange?.({
      keyword: '',
      statuses: [],
      tags: [],
      processingTimes: [],
      tagFilterOption: undefined,
      processingTimeFilterOption: undefined,
      assignees: [],
      assigneeFilterOption: undefined,
    })
  }, [handleChange])

  return {
    keyword,
    tags,
    tagFilterItems,
    tagFilterOption,
    processingTimes,
    processingTimeFilterItems,
    processingTimeFilterOption,
    caseTypes,
    caseTypeFilterItems,
    statuses,
    statusFilterItems,
    assignees,
    assigneeFilterItems,
    assigneeFilterOption,
    disabled,
    isFilterApplied,
    handleChangeKeyword,
    handleChangeStatuses,
    handleChangeTags,
    handleChangeTagFilterOption,
    handleChangeProcessingTimes,
    handleChangeProcessingTimesFilterOption,
    handleChangeCaseTypes,
    handleChangeAssignees,
    handleChangeAssigneesFilterOption,
    handleReset,
  }
}
